import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fmtDate, inr, P, type FnfSettlement, type ID } from '@hrms/shared';
import { useAuth } from '../../../auth/AuthProvider';
import { api } from '../../../lib/api';
import { todayISO } from '../../../lib/date';
import { fieldErrors } from '../../../lib/errors';
import { useMut } from '../../../lib/query';
import { useParams } from '../../../navigation/nav';
import { Button, DataTable, DateField, Divider, Modal, PageHeader, Row, Screen, Stack, StatusBadge, Text, useTheme } from '../../../ui';
import { AutoGrid, AmountLine, KeyValue, NumField } from '../components/common';
import { EmployeePicker, PersonCell } from '../components/EmployeePicker';

type Form = { employee_id: ID | null; last_working_day: string; resignation_date: string; notice_recovery_days: number; other_earnings: number; other_deductions: number };

function SettlementView({ s }: { s: FnfSettlement }) {
  const { colors } = useTheme();
  return (
    <Stack gap={14} testID="fnf-settlement">
      <Row gap={24}>
        <KeyValue label="Resignation" value={fmtDate(s.resignation_date)} />
        <KeyValue label="Last working day" value={fmtDate(s.last_working_day)} />
        <KeyValue label="Status" value={<StatusBadge status={s.status} />} />
      </Row>
      <DataTable
        rows={s.lines}
        keyExtractor={(_, i) => i}
        emptyTitle="No lines"
        columns={[
          { key: 'label', title: 'Item', width: 200, flex: 2, sortable: false },
          { key: 'type', title: 'Type', width: 110, sortable: false, render: (l) => (l.type === 'earning' ? 'Earning' : 'Deduction') },
          { key: 'amount', title: 'Amount', width: 120, align: 'right', sortable: false, render: (l) => <Text color={l.type === 'deduction' ? colors.danger : undefined}>{`${l.type === 'deduction' ? '−' : ''}${inr(l.amount)}`}</Text> },
        ]}
      />
      <Stack gap={0}>
        <AmountLine label="Total earnings" value={inr(s.total_earnings)} />
        <AmountLine label="Total recoveries & deductions" value={inr(s.total_deductions)} />
        <Divider spacing={6} />
        <AmountLine label={s.net_payable >= 0 ? 'Net payable to employee' : 'Net recoverable from employee'} value={inr(Math.abs(s.net_payable))} strong color={s.net_payable >= 0 ? colors.success : colors.danger} />
      </Stack>
    </Stack>
  );
}

export function FnfScreen() {
  const { can } = useAuth();
  const manage = can(P.PAYROLL_MANAGE);
  const initialEmp = Number(useParams<{ employee: string }>().employee) || null;
  const q = useQuery({ queryKey: ['fnf'], queryFn: () => api.payroll.fnf.list() });
  const blank = (emp: ID | null): Form => ({ employee_id: emp, last_working_day: todayISO(), resignation_date: '', notice_recovery_days: 0, other_earnings: 0, other_deductions: 0 });
  const [open, setOpen] = useState(!!initialEmp);
  const [f, setF] = useState<Form>(blank(initialEmp));
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [sel, setSel] = useState<FnfSettlement | null>(null);
  const compute = useMut(() => api.payroll.fnf.compute({ ...f, employee_id: f.employee_id!, resignation_date: f.resignation_date || undefined }), {
    invalidate: [['fnf']],
    success: 'Settlement computed',
    onSuccess: (r) => {
      setOpen(false);
      setSel(r);
      setErrs({});
    },
    onError: (e) => setErrs(fieldErrors(e)),
  });
  const approve = useMut((id: ID) => api.payroll.fnf.approve(id), { invalidate: [['fnf']], success: 'Settlement approved', onSuccess: (r) => setSel(r) });
  const num = (k: 'notice_recovery_days' | 'other_earnings' | 'other_deductions', label: string, hint?: string) => (
    <NumField label={label} hint={hint} error={errs[k]} value={f[k]} onChange={(v) => setF({ ...f, [k]: v ?? 0 })} />
  );
  return (
    <Screen refreshing={q.isRefetching} onRefresh={() => q.refetch()} testID="fnf">
      <PageHeader
        title="Full & final settlement"
        subtitle="Pending salary, leave encashment, gratuity, notice recovery and other deductions for exiting employees."
        actions={
          manage ? (
            <Button
              variant="primary"
              icon="calc"
              title="New settlement"
              onPress={() => {
                setF(blank(null));
                setErrs({});
                setOpen(true);
              }}
              testID="new-fnf"
            />
          ) : undefined
        }
      />
      <DataTable
        rows={q.data}
        loading={q.isLoading}
        onRowPress={setSel}
        emptyTitle="No settlements"
        columns={[
          { key: 'employee', title: 'Employee', width: 200, flex: 2, render: (s) => <PersonCell emp={s.employee} fallback={s.employee_id} />, sortValue: (s) => s.employee?.full_name },
          { key: 'last_working_day', title: 'Last working day', width: 130, render: (s) => fmtDate(s.last_working_day), sortValue: (s) => s.last_working_day },
          { key: 'total_earnings', title: 'Earnings', width: 120, align: 'right', render: (s) => inr(s.total_earnings), sortValue: (s) => s.total_earnings },
          { key: 'total_deductions', title: 'Deductions', width: 120, align: 'right', render: (s) => inr(s.total_deductions), sortValue: (s) => s.total_deductions },
          { key: 'net_payable', title: 'Net', width: 120, align: 'right', render: (s) => <Text weight="semibold">{inr(s.net_payable)}</Text>, sortValue: (s) => s.net_payable },
          { key: 'status', title: 'Status', width: 100, render: (s) => <StatusBadge status={s.status} />, sortValue: (s) => s.status },
        ]}
      />
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Compute F&F settlement"
        width={620}
        testID="fnf-form"
        footer={
          <>
            <Button title="Cancel" onPress={() => setOpen(false)} />
            <Button variant="primary" icon="calc" title="Compute" disabled={!f.employee_id || !f.last_working_day} loading={compute.isPending} onPress={() => compute.mutate(undefined)} testID="fnf-compute" />
          </>
        }
      >
        <EmployeePicker label="Employee" required value={f.employee_id} error={errs.employee_id} onChange={(v) => setF({ ...f, employee_id: v })} />
        <AutoGrid min={220}>
          <DateField label="Resignation date" clearable value={f.resignation_date} error={errs.resignation_date} onChange={(v) => setF({ ...f, resignation_date: v })} />
          <DateField label="Last working day" required value={f.last_working_day} error={errs.last_working_day} onChange={(v) => setF({ ...f, last_working_day: v })} />
          {num('notice_recovery_days', 'Notice shortfall (days)', 'Days of notice pay to recover')}
          {num('other_earnings', 'Other earnings ₹', 'Bonus, reimbursements due')}
          {num('other_deductions', 'Other deductions ₹', 'Loans, assets not returned')}
        </AutoGrid>
      </Modal>
      <Modal
        open={!!sel}
        onClose={() => setSel(null)}
        title={sel?.employee?.full_name ?? 'Settlement'}
        width={620}
        footer={
          <>
            <Button title="Close" onPress={() => setSel(null)} />
            {sel?.status === 'draft' && manage && <Button variant="success" icon="check" title="Approve settlement" loading={approve.isPending} onPress={() => approve.mutate(sel.id)} testID="fnf-approve" />}
          </>
        }
      >
        {sel && <SettlementView s={sel} />}
      </Modal>
    </Screen>
  );
}
